/**
 * Widget initialization logic
 */

import type { SelectElement, WidgetState } from './types';
import type { Country, Subdivision } from '@countriesdb/widget-core';
import { CountriesDBClient } from '@countriesdb/widget-core';
import {
  initializeSelect,
  populateCountrySelect,
  buildSubdivisionOptionsHTML,
  applyPreselectedValue,
  handleApiError,
  parseBoolean,
} from './dom-manipulation';
import { dispatchUpdateEvent, isWidgetInitiatedEvent } from './event-system';
import {
  triggerFollowLogic,
  handleFollowRelatedFromSubdivision,
  handleFollowUpwardFromSubdivision,
  handleFollowUpwardFromCountry,
} from './follow-logic';

/**
 * Setup subdivision selection elements
 */
export async function setupSubdivisionSelection(
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  const subdivisionSelects = Array.from(
    document.querySelectorAll<SelectElement>('.subdivision-selection')
  );

  for (const select of subdivisionSelects) {
    // Initialize with a default option
    initializeSelect(select, '&mdash;', true);

    // Linked country select (if any)
    const countryName = select.dataset.country;
    const linkedCountrySelect = countryName
      ? document.querySelector<SelectElement>(
          `.country-selection[data-name="${countryName}"]`
        )
      : null;

    // Check if linked country select is multi-select (not allowed)
    if (linkedCountrySelect && linkedCountrySelect.hasAttribute('multiple')) {
      handleApiError(select, 'Cannot link to multi-select country. Use data-country-code instead.', true);
      continue;
    }

    // No direct link → maybe data-country-code
    if (!countryName || !linkedCountrySelect) {
      if (
        select.hasAttribute('data-country-code') &&
        select.dataset.countryCode
      ) {
        await updateSubdivisionSelect(
          select,
          apiKey,
          backendUrl,
          state,
          config,
          select.dataset.countryCode
        );
      } else {
        handleApiError(select, 'No country select present');
      }
    }

    // Always dispatch an update event for user-initiated subdivision changes
    select.addEventListener('change', (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      dispatchUpdateEvent(select, { type: 'subdivision', reason: 'regular' });
    });

    // --- follow_related (forward direction) ---
    select.addEventListener('change', async (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      await handleFollowRelatedFromSubdivision(
        select,
        apiKey,
        backendUrl,
        state,
        config.followRelated,
        (s, key, code) =>
          updateSubdivisionSelect(s, key, backendUrl, state, config, code)
      );
    });

    // --- follow_upward (reverse direction) ---
    select.addEventListener('change', async (event: Event) => {
      if (isWidgetInitiatedEvent(event)) {
        return;
      }
      await handleFollowUpwardFromSubdivision(
        select,
        apiKey,
        backendUrl,
        state,
        config.followUpward,
        (s, key, code) =>
          updateSubdivisionSelect(s, key, backendUrl, state, config, code)
      );
    });
  }
}

/**
 * Update subdivision select with data for a specific country
 */
export async function updateSubdivisionSelect(
  select: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    forcedLanguage?: string;
    defaultLanguage?: string;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  },
  countryCode: string
): Promise<void> {
  const preselectedValue =
    select.getAttribute('data-preselected') || select.dataset.preselected;

  // Check if this is a reload (select was already populated before)
  const isReload = state.subdivisionsMap.has(select);

  initializeSelect(select, '&mdash;', true);

  // Mark as initializing to prevent change events
  state.isInitializing.add(select);

  let effectiveCountryCode = countryCode;
  if (!effectiveCountryCode) {
    effectiveCountryCode = select.dataset.countryCode || '';
  }

  if (effectiveCountryCode) {
    let valueSetByWidget = false; // Track if a value was set by widget
    try {
      // Use GeoIP only if data-preselected attribute is not set at all
      const shouldUseGeoIP =
        preselectedValue === undefined || preselectedValue === null;

      // Check if this subdivision select prefers official subdivisions
      const preferOfficial =
        select.hasAttribute('data-prefer-official') ||
        config.preferOfficialSubdivisions;

      const languageHeaders = CountriesDBClient.getLanguageHeaders(
        config.forcedLanguage,
        config.defaultLanguage
      );

      const subdivisionsResult = await CountriesDBClient.fetchSubdivisions({
        apiKey,
        backendUrl,
        countryCode: effectiveCountryCode,
        shouldUseGeoIP,
        preferOfficial,
        subdivisionRomanizationPreference: config.subdivisionRomanizationPreference,
        preferLocalVariant: config.preferLocalVariant,
        languageHeaders,
      });

      const subdivisions = subdivisionsResult.data;
      const subdivisionsLanguage = subdivisionsResult.language || 'en';

      // Store in memory
      state.subdivisionsMap.set(select, subdivisions);
      state.subdivisionsLanguageMap.set(select, subdivisionsLanguage);

      if (subdivisions.length > 0) {
        // Populate <option>
        select.disabled = false;

        // Preserve data attributes before setting innerHTML (only for non-multi-select)
        const isMultiple = select.hasAttribute('multiple');
        const dataLabel = !isMultiple ? select.dataset.label : undefined;
        const dataDefaultValue = !isMultiple
          ? select.dataset.defaultValue
          : undefined;

        select.innerHTML = buildSubdivisionOptionsHTML(
          subdivisions,
          select,
          subdivisionsLanguage,
          config.showSubdivisionType,
          config.allowParentSelection,
          config.subdivisionNameFilter
        );

        // Restore data attributes after setting innerHTML
        if (!isMultiple) {
          if (dataLabel !== undefined) {
            select.dataset.label = dataLabel;
          }
          if (dataDefaultValue !== undefined) {
            select.dataset.defaultValue = dataDefaultValue;
          }
        }

        // Manual preselection wins
        // Check if preselected value exists (applyPreselectedValue will read it from select element)
        const hasPreselectedValue = 
          (select.getAttribute('data-preselected') || select.dataset.preselected || select.dataset._widgetTempPreselect) !== undefined;
        if (hasPreselectedValue) {
          applyPreselectedValue(select, apiKey);
          dispatchUpdateEvent(select, {
            type: 'subdivision',
            reason: 'preselected',
          });
          valueSetByWidget = true;
          await triggerFollowLogic(
            select,
            apiKey,
            backendUrl,
            state,
            config.followRelated,
            config.followUpward,
            (s, key, code) =>
              updateSubdivisionSelect(s, key, backendUrl, state, config, code),
            (countrySelect, key) =>
              updateSubdivisions(countrySelect, key, backendUrl, state, config)
          );
        } else {
          // Try GeoIP preselect
          if (shouldUseGeoIP) {
            const preselectedSubdivision = subdivisions.find(
              (subdivision) => subdivision.preselected
            );
            if (preselectedSubdivision) {
              const isMultiple = select.hasAttribute('multiple');
              if (isMultiple) {
                // For multi-select, find and select the option
                const option = Array.from(select.options).find(
                  (opt) => opt.value === preselectedSubdivision.code
                );
                if (option) {
                  option.selected = true;
                }
              } else {
                // Single select: set value directly
                select.value = preselectedSubdivision.code;
              }
              dispatchUpdateEvent(select, {
                type: 'subdivision',
                reason: 'geoip',
              });
              valueSetByWidget = true;
              await triggerFollowLogic(
                select,
                apiKey,
                backendUrl,
                state,
                config.followRelated,
                config.followUpward,
                (s, key, code) =>
                  updateSubdivisionSelect(s, key, backendUrl, state, config, code),
                (countrySelect, key) =>
                  updateSubdivisions(countrySelect, key, backendUrl, state, config)
              );
            }
          }
        }
      } else {
        select.disabled = true;
      }
    } catch (error) {
      console.error('Failed to fetch subdivisions:', error);
      handleApiError(select, error as Error);
    } finally {
      // Mark initialization as complete
      state.isInitializing.delete(select);
      // Only fire 'reload' if this is a reload, not initial load
      if (isReload && !valueSetByWidget) {
        dispatchUpdateEvent(select, {
          type: 'subdivision',
          reason: 'reload',
        });
      }
    }
  } else if (
    !select.dataset.country ||
    !document.querySelector(
      `.country-selection[data-name="${select.dataset.country}"]`
    )
  ) {
    handleApiError(select, 'No country select present');
  }
}

/**
 * Update subdivisions for all linked subdivision selects when country changes
 */
export async function updateSubdivisions(
  countrySelect: SelectElement,
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    forcedLanguage?: string;
    defaultLanguage?: string;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  // Don't update subdivisions for multi-select countries (not supported)
  if (countrySelect.hasAttribute('multiple')) {
    return;
  }

  const selectedCountry = countrySelect.value;
  const linkedSubdivisionSelects = Array.from(
    document.querySelectorAll<SelectElement>(
      `.subdivision-selection[data-country="${countrySelect.dataset.name}"]`
    )
  );

  for (const select of linkedSubdivisionSelects) {
    await updateSubdivisionSelect(
      select,
      apiKey,
      backendUrl,
      state,
      config,
      selectedCountry
    );
  }
}

/**
 * Setup country selection elements
 */
export async function setupCountrySelection(
  apiKey: string,
  backendUrl: string,
  state: WidgetState,
  config: {
    defaultLanguage?: string;
    forcedLanguage?: string;
    isoCountryNames: boolean;
    followRelated: boolean;
    followUpward: boolean;
    countryNameFilter?: (
      code: string,
      name: string,
      language: string,
      item: Country
    ) => string | false | null | undefined;
  },
  subdivisionConfig: {
    followRelated: boolean;
    followUpward: boolean;
    showSubdivisionType: boolean;
    allowParentSelection: boolean;
    preferOfficialSubdivisions: boolean;
    subdivisionRomanizationPreference?: string;
    preferLocalVariant: boolean;
    subdivisionNameFilter?: (
      code: string,
      originalName: string,
      language: string,
      countryCode: string | null,
      item: Subdivision
    ) => string | false | null | undefined;
  }
): Promise<void> {
  const countrySelects = Array.from(
    document.querySelectorAll<SelectElement>('.country-selection')
  );
  const seenNames: Record<string, boolean> = {}; // track data-name to detect duplicates

  for (const select of countrySelects) {
    const name = select.dataset.name;

    // Duplicates
    if (name && seenNames[name]) {
      select.removeAttribute('data-name');
      initializeSelect(select, '&mdash;');
      handleApiError(select, 'Duplicate field');
      continue;
    }
    if (name) {
      seenNames[name] = true;
    }

    // Note: Class renaming is now handled earlier in fixMisClassedElements()
    // This check is kept as a safety net but should not be needed

    // Initialize
    initializeSelect(select, '&mdash;');

    // Mark as initializing to prevent change events
    state.isInitializing.add(select);

    let valueSetByWidget = false; // Track if value was set by widget
    let loadedInitialSubdivisions = false; // Track if subdivisions were loaded

    try {
      const preselectedValue =
        select.getAttribute('data-preselected') || select.dataset.preselected;
      // Use GeoIP only if data-preselected attribute is not set at all
      const shouldUseGeoIP =
        preselectedValue === undefined || preselectedValue === null;

      const languageHeaders = CountriesDBClient.getLanguageHeaders(
        config.forcedLanguage,
        config.defaultLanguage
      );

      // Fetch & populate countries
      const countriesResult = await CountriesDBClient.fetchCountries({
        apiKey,
        backendUrl,
        shouldUseGeoIP,
        isoCountryNames: config.isoCountryNames,
        languageHeaders,
      });

      const countries = countriesResult.data;
      const countriesLanguage = countriesResult.language || 'en';

      state.countriesMap.set(select, countries);
      populateCountrySelect(
        select,
        countries,
        countriesLanguage,
        config.countryNameFilter
      );

      // Apply preselected (manual)
      // Check if preselected value exists (applyPreselectedValue will read it from select element)
      const hasPreselectedValue = 
        (select.getAttribute('data-preselected') || select.dataset.preselected || select.dataset._widgetTempPreselect) !== undefined;
      if (hasPreselectedValue) {
        applyPreselectedValue(select, apiKey);
        dispatchUpdateEvent(select, { type: 'country', reason: 'preselected' });
        valueSetByWidget = true;
        // Load subdivisions after applying preselected value
        if (
          select.value &&
          select.value !== (select.dataset.defaultValue || '')
        ) {
          // This will be handled by the updateSubdivisions call below
          // We need to pass the config for subdivisions
        }
      }

      // GeoIP auto-select
      if (shouldUseGeoIP) {
        const preselectedCountry = countries.find(
          (country) => country.preselected
        );
        if (preselectedCountry) {
          select.value = preselectedCountry.iso_alpha_2;
          dispatchUpdateEvent(select, { type: 'country', reason: 'geoip' });
          valueSetByWidget = true;
        }
      }

      // If already chosen, load subdivisions
      if (
        !loadedInitialSubdivisions &&
        select.value &&
        select.value !== (select.dataset.defaultValue || '')
      ) {
        // Subdivisions will be loaded by event handler
        loadedInitialSubdivisions = true;
      }

      // On change => update subdivisions
      select.addEventListener('change', async (event: Event) => {
        if (isWidgetInitiatedEvent(event)) {
          return;
        }

        // Dispatch update event for user-initiated country change
        dispatchUpdateEvent(select, { type: 'country', reason: 'regular' });

        // Update subdivisions
        await updateSubdivisions(select, apiKey, backendUrl, state, subdivisionConfig);

        const chosen = select.value;
        if (!chosen) {
          return;
        }

        const stored = state.countriesMap.get(select) || [];
        const picked = stored.find((c) => c.iso_alpha_2 === chosen);
        if (!picked) {
          return;
        }

        // followUpward from country perspective
        // Only works when country is single-select (never for multi-select)
        if (config.followUpward && !select.multiple && picked.is_subdivision_of) {
          await handleFollowUpwardFromCountry(
            select,
            apiKey,
            backendUrl,
            state,
            config.followUpward,
            (s, key, code) =>
              updateSubdivisionSelect(s, key, backendUrl, state, subdivisionConfig, code)
          );
        }
      });
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      handleApiError(select, error as Error);
      // Handle subdivision errors
      if (select.dataset.name) {
        const linkedSubdivisionSelects = Array.from(
          document.querySelectorAll<SelectElement>(
            `.subdivision-selection[data-country="${select.dataset.name}"]`
          )
        );
        for (const s of linkedSubdivisionSelects) {
          initializeSelect(s, '&mdash;');
          handleApiError(s, error as Error);
        }
      }
    } finally {
      // Mark initialization as complete
      state.isInitializing.delete(select);
      // If no preselected and no geoip selection happened, emit a regular update
      if (!valueSetByWidget) {
        dispatchUpdateEvent(select, { type: 'country', reason: 'regular' });
      }
    }
  }
}

